import React, { FunctionComponent } from 'react';
import { useLocation } from 'react-router-dom';
import AutoAwesomeIcon from 'src/shared/material_icons/image/auto_awesome.svg';
import theme from 'src/shared/theme';
import styled from 'styled-components';
import { useAgentAssistant } from '../agentAssistant';
import { T } from './helpers';

// A launcher, not a route: it opens Zakeke AI full screen. It sits at the top of the sidebar, above the search and the
// menu, and reads as a small banner rather than a menu row so it stands apart from the navigation below it.
const BannerRow = styled.div`
    margin: 0 12px 10px;
`;

const Banner = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    box-sizing: border-box;
    /* Same height as the solution switcher trigger right above it (8px padding + its 26px icon + border). */
    height: 44px;
    padding: 0 10px;
    overflow: hidden;
    border: 1px solid ${theme.ai.calloutBorder};
    border-radius: 8px;
    background: ${theme.ai.calloutSurface};
    font-family: inherit;
    text-align: left;
    cursor: pointer;
    transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease;

    &:hover {
        border-color: ${theme.ai.gradientMid};
        box-shadow: 0 1px 6px rgba(124, 58, 237, 0.18);
    }
`;

const BannerIcon = styled.span`
    display: grid;
    place-items: center;
    flex: none;
    width: 26px;
    height: 26px;
    border-radius: 6px;
    background: ${theme.ai.gradient};

    svg {
        width: 15px;
        height: 15px;
        fill: #ffffff;
    }
`;

const BannerText = styled.span`
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

const BannerTitle = styled.span`
    font-size: 12px;
    font-weight: 600;
    line-height: 1.2;
    color: ${theme.ai.calloutText};
`;

// Two lines at most, so the caption never pushes the banner past the fixed height.
const BannerCaption = styled.small`
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    font-size: 10px;
    line-height: 1.2;
    color: #6b7280;
`;

/** AI Agents Studio is itself an AI workspace: its own prompt is the one to use there. */
const isAiStudio = (pathname: string) => pathname.toLowerCase().includes('/ai-creative-tools/studio');

const SidebarAiBanner: FunctionComponent = () => {
    const { isEnabled, openAiMode } = useAgentAssistant();
    const { pathname } = useLocation();
    if (!isEnabled || isAiStudio(pathname)) return null;

    return (
        <BannerRow>
            <Banner type='button' onClick={openAiMode}>
                <BannerIcon>
                    <AutoAwesomeIcon />
                </BannerIcon>
                <BannerText>
                    <BannerTitle>{T._('Zakeke AI', 'Admin')}</BannerTitle>
                    <BannerCaption>{T._('Your Zakeke back office, driven by AI.', 'Admin')}</BannerCaption>
                </BannerText>
            </Banner>
        </BannerRow>
    );
};

export default SidebarAiBanner;
